// Téléversement de la photo : POST /api/upload-url puis PUT DIRECT vers Azure Blob Storage.
// Jamais d'image en base64 dans le corps de l'API (README « Partis pris ») — l'API ne voit que le
// `blobId`, l'octet passe du téléphone au Blob sans transiter par le Container App.
import type { UploadUrlResponse } from './types.js';

/** Échec du téléversement : message déjà destiné à l'utilisateur, jamais une trace technique. */
export class UploadError extends Error {}

export interface OptionsUpload {
  baseUrl?: string;
  signal?: AbortSignal;
  fetchImpl?: typeof fetch;
}

/**
 * Rend le `blobId` à passer à POST /api/analyze.
 * Le header `x-ms-blob-type: BlockBlob` est obligatoire côté Azure Blob : sans lui le PUT part en
 * 400 alors que la SAS est valide.
 */
export async function televerserPhoto(photo: Blob, options: OptionsUpload = {}): Promise<string> {
  const fetchImpl = options.fetchImpl ?? fetch;
  const base = options.baseUrl ?? '';

  let reponse: Response;
  try {
    reponse = await fetchImpl(`${base}/api/upload-url`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({}),
      signal: options.signal,
    });
  } catch {
    throw new UploadError("Impossible de joindre NutriScan : vérifie ta connexion, puis réessaie.");
  }
  if (!reponse.ok) {
    throw new UploadError("L'envoi de la photo est indisponible pour le moment.");
  }

  const { blobId, uploadUrl } = (await reponse.json()) as UploadUrlResponse;
  if (!blobId || !uploadUrl) throw new UploadError("L'envoi de la photo est indisponible pour le moment.");

  let put: Response;
  try {
    put = await fetchImpl(uploadUrl, {
      method: 'PUT',
      headers: {
        'x-ms-blob-type': 'BlockBlob',
        'Content-Type': photo.type || 'application/octet-stream',
      },
      body: photo,
      signal: options.signal,
    });
  } catch {
    throw new UploadError("L'envoi de la photo a échoué : vérifie ta connexion, puis réessaie.");
  }
  if (!put.ok) {
    // Cas le plus fréquent en production : la SAS a expiré (TTL court) entre la capture et le PUT.
    throw new UploadError("L'envoi de la photo a échoué : reprends la photo.");
  }

  return blobId;
}
